import { useState } from "react";
import { ArrowLeft, Mail, Copy, Check, ClipboardList, FileText, AlertCircle, MessageSquare, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/landing/Footer";

const emailTemplate = `Empresa:
CNPJ:
Responsável:

Tipo de solicitação:
( ) Nova licença
( ) Conversão de Trial
( ) Renovação de licença

Possui o executável instalado?
( ) Sim
( ) Não

Machine ID (se aplicável):

[Anexar comprovante de pagamento]`;

export default function ConfirmacaoPagamentoPage() {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(emailTemplate);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E5E7EB]">
      {/* Top Nav */}
      <div className="max-w-4xl mx-auto px-6 pt-6">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#9CA3AF] transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para o site
        </a>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        {/* Confirmation Header */}
        <div className="text-center mb-16" data-testid="confirmation-header">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#0E3A46] rounded-full">
            <Check className="h-8 w-8 text-[#F59E0B]" />
          </div>

          <span className="inline-block px-4 py-1 text-xs font-mono tracking-widest uppercase text-[#F59E0B] bg-[#0E3A46] border border-[#F59E0B]/20 rounded-sm mb-6">
            Confirmação de Pagamento
          </span>

          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Pagamento recebido com <span className="text-[#F59E0B]">sucesso</span>
          </h1>

          <p className="text-[#9CA3AF] text-base md:text-lg max-w-2xl mx-auto">
            Obrigado por adquirir a licença do KpAction. Seu pagamento foi registrado com sucesso.
            Para que possamos gerar sua chave de ativação, é necessário apenas enviar algumas informações para nossa equipe.
          </p>
        </div>

        {/* Próximos Passos */}
        <section className="mb-12" data-testid="next-steps-section">
          <div className="flex items-center gap-3 mb-6">
            <ClipboardList className="h-5 w-5 text-[#F59E0B]" />
            <h2 className="font-heading text-xl font-bold">Próximos passos</h2>
          </div>

          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6">
            <p className="text-sm text-[#9CA3AF] mb-4">Envie um e-mail para:</p>
            <a
              href="mailto:contato@kpaction.com.br"
              className="inline-flex items-center gap-2 text-[#F59E0B] hover:text-[#FBBF24] transition-colors font-medium"
              data-testid="contact-email-link"
            >
              <Mail className="h-4 w-4" />
              contato@kpaction.com.br
            </a>
            <p className="text-sm text-[#9CA3AF] mt-4">Incluindo as seguintes informações:</p>
          </div>
        </section>

        {/* Informações Obrigatórias */}
        <section className="mb-12" data-testid="required-info-section">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="h-5 w-5 text-[#F59E0B]" />
            <h2 className="font-heading text-xl font-bold">Informações obrigatórias</h2>
          </div>

          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6">
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Nome da empresa
              </li>
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                CNPJ
              </li>
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Nome do responsável
              </li>
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Comprovante de pagamento
              </li>
            </ul>
          </div>
        </section>

        {/* Caso já tenha utilizado */}
        <section className="mb-12" data-testid="trial-info-section">
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6">
            <p className="text-sm font-medium text-[#E5E7EB] mb-3">
              Caso já tenha utilizado o software
            </p>
            <p className="text-sm text-[#9CA3AF] mb-4">
              Se você já utilizou o período de avaliação (trial) ou já possui o executável instalado, inclua também no e-mail:
            </p>
            <div className="inline-block bg-[#0F1115] border border-[#2A2F38] rounded-sm px-4 py-2">
              <p className="text-sm text-[#F59E0B] font-mono">Machine ID</p>
            </div>
          </div>
        </section>

        {/* Tipo de solicitação */}
        <section className="mb-12" data-testid="request-type-section">
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6">
            <p className="text-sm font-medium text-[#E5E7EB] mb-3">
              Informe também o tipo de solicitação
            </p>
            <p className="text-sm text-[#9CA3AF] mb-4">
              No e-mail, indique qual é o seu caso:
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Conversão de Trial para Licença Anual
              </li>
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Nova Licença
              </li>
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Renovação de Licença
              </li>
            </ul>
            <p className="text-sm text-[#9CA3AF] mt-4 border-t border-[#2A2F38] pt-4">
              Caso ainda não possua o executável, informe isso no e-mail para que possamos enviar o link de download.
            </p>
          </div>
        </section>

        {/* Validação e envio */}
        <section className="mb-12" data-testid="validation-section">
          <div className="flex items-center gap-3 mb-6">
            <Check className="h-5 w-5 text-[#F59E0B]" />
            <h2 className="font-heading text-xl font-bold">Validação e envio da licença</h2>
          </div>

          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6 space-y-4">
            <p className="text-sm text-[#9CA3AF]">
              Após a validação do pagamento e das informações enviadas, você receberá no mesmo e-mail:
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Arquivo contendo a chave de ativação da licença
              </li>
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0" />
                Caso necessário, um link para download do executável
              </li>
            </ul>
            <p className="text-sm text-[#9CA3AF]">
              O link de download incluirá também um arquivo com:
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#6B7280] rounded-full flex-shrink-0" />
                Instruções completas de instalação
              </li>
              <li className="flex items-center gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#6B7280] rounded-full flex-shrink-0" />
                Instruções detalhadas de ativação da licença
              </li>
            </ul>
          </div>
        </section>

        {/* Importante */}
        <section className="mb-12" data-testid="important-section">
          <div className="flex items-center gap-3 mb-6">
            <AlertCircle className="h-5 w-5 text-[#F59E0B]" />
            <h2 className="font-heading text-xl font-bold">Importante</h2>
          </div>

          <div className="bg-[#1C1F26] border border-[#F59E0B]/20 rounded-sm p-6">
            <p className="text-sm text-[#9CA3AF] mb-4">
              Para garantir que o processo ocorra corretamente:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0 mt-1.5" />
                Utilize o mesmo e-mail usado na compra
              </li>
              <li className="flex items-start gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0 mt-1.5" />
                Anexe o comprovante de pagamento
              </li>
              <li className="flex items-start gap-3 text-sm text-[#9CA3AF]">
                <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full flex-shrink-0 mt-1.5" />
                Informe corretamente o Machine ID caso já utilize o software
              </li>
            </ul>
          </div>
        </section>

        {/* Modelo de E-mail */}
        <section className="mb-12" data-testid="email-template-section">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="h-5 w-5 text-[#F59E0B]" />
            <h2 className="font-heading text-xl font-bold">Modelo de e-mail</h2>
          </div>

          <p className="text-sm text-[#9CA3AF] mb-4">
            Você pode copiar o modelo abaixo ao enviar o e-mail:
          </p>

          <div className="bg-[#0A0B0E] border border-[#2A2F38] rounded-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[#1C1F26] border-b border-[#2A2F38]">
              <div>
                <p className="text-xs font-mono tracking-wider uppercase text-[#6B7280]">Assunto do e-mail</p>
                <p className="text-sm text-[#E5E7EB] mt-1">Licença KpAction – Ativação</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="border-[#2A2F38] text-[#9CA3AF] hover:bg-[#2A2F38] hover:text-[#E5E7EB]"
                data-testid="copy-template-btn"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copiar
                  </>
                )}
              </Button>
            </div>
            <pre className="p-5 text-sm text-[#9CA3AF] font-mono whitespace-pre-wrap leading-relaxed" data-testid="email-template-content">
{emailTemplate}
            </pre>
          </div>
        </section>

        {/* Contato */}
        <section className="mb-16" data-testid="contact-section">
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-6 text-center">
            <p className="text-sm text-[#9CA3AF] mb-3">Em caso de dúvidas, entre em contato:</p>
            <a
              href="mailto:contato@kpaction.com.br"
              className="inline-flex items-center gap-2 text-[#F59E0B] hover:text-[#FBBF24] transition-colors font-medium"
              data-testid="contact-email-footer"
            >
              <Mail className="h-4 w-4" />
              contato@kpaction.com.br
            </a>
          </div>
        </section>

        {/* Mensagem Institucional */}
        <section className="mb-16 text-center" data-testid="institutional-section">
          <div className="border-t border-[#2A2F38] pt-12 max-w-2xl mx-auto">
            <p className="text-sm text-[#9CA3AF] leading-relaxed mb-4">
              O KpAction foi desenvolvido para oferecer visibilidade estrutural da operação, permitindo que gestores identifiquem tendências operacionais e riscos antes que se tornem problemas críticos.
            </p>
            <p className="text-sm text-[#6B7280]">
              Agradecemos pela sua confiança.
            </p>
          </div>
        </section>

        {/* Rodapé Institucional */}
        <div className="border-t border-[#2A2F38] pt-8 text-center" data-testid="institutional-footer">
          <p className="font-heading text-lg font-bold mb-1">
            <span className="text-[#F59E0B]">Kp</span><span className="text-[#E5E7EB]">Action</span>
          </p>
          <p className="text-xs text-[#6B7280] mb-4">
            Inteligência operacional para empresas que buscam crescimento estruturado.
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-[#6B7280]">
            <Building2 className="h-3.5 w-3.5" />
            <span>EVIDENTIA TECNOLOGIA LTDA — CNPJ: 65.097.807/0001-30 — São Paulo — SP</span>
          </div>
        </div>
      </main>
    </div>
  );
}
